'use client';

import Image from 'next/image';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowUpRight, X } from 'lucide-react';
import { StudioAction } from '@/components/ui/StudioAction';
import { buildLocalizedSeoPath } from '@/lib/seo/site';
import { normalizeLocale } from '@/i18n/locales';
import { createPairedPlayback, type PairedPlaybackState } from './paired-video-playback';
import cases from './kagemane-showcase-cases.json';
import base from './StudioHomepage.module.css';
import styles from './KagemaneShowcase.module.css';

type ShowcaseCase = (typeof cases)[number];
const initialPlayback: PairedPlaybackState = { playing: false, loading: false, error: false, currentTime: 0, duration: 0 };

function CaseComparison({ item }: { item: ShowcaseCase }) {
  const t = useTranslations('auth.cinematic.studioHomepage.work.motion');
  const referenceLabel = t('viewReferences', { count: item.references.length });
  const stage = useRef<HTMLDivElement>(null);
  const original = useRef<HTMLVideoElement>(null);
  const recreated = useRef<HTMLVideoElement>(null);
  const transport = useRef<ReturnType<typeof createPairedPlayback> | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const resumeAfterReference = useRef(false);
  const [playback, setPlayback] = useState(initialPlayback);
  const [referenceIndex, setReferenceIndex] = useState(0);

  useEffect(() => {
    if (!original.current || !recreated.current || !stage.current) return;
    const pair = createPairedPlayback(original.current, recreated.current, setPlayback);
    transport.current = pair;
    let visible = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = async () => {
      if (!visible || document.hidden || dialog.current?.open) pair.pause();
      else if (!reducedMotion.matches) await pair.play();
    };
    const observer = new IntersectionObserver(async ([entry]) => {
      visible = entry.isIntersecting;
      await update();
    }, { threshold: .25 });
    observer.observe(stage.current);
    document.addEventListener('visibilitychange', update);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
      pair.dispose();
      transport.current = null;
    };
  }, []);

  const openReference = (index: number) => {
    setReferenceIndex(index);
    resumeAfterReference.current = playback.playing || playback.loading;
    transport.current?.pause();
    dialog.current?.showModal();
  };

  return <div ref={stage} className={styles.comparison} data-kagemane-comparison={item.key} data-layout={item.aspectRatio > 1 ? 'landscape' : 'portrait'}>
    <div className={styles.videos}>
      {(['original', 'recreated'] as const).map(kind => <figure key={kind}>
        <figcaption>{t(kind === 'original' ? 'originalLabel' : 'recreatedLabel')}</figcaption>
        <div className={styles.videoFrame}>
          <video ref={kind === 'original' ? original : recreated} src={item[kind].src} poster={item[kind].poster}
            muted playsInline preload="metadata" aria-label={t(kind === 'original' ? 'originalLabel' : 'recreatedLabel')} />
          {kind === 'recreated' && <button type="button" className={styles.referencePeek} onClick={() => openReference(0)}
            aria-label={referenceLabel} aria-haspopup="dialog" title={referenceLabel}>
            <Image unoptimized src={item.references[0].url} alt="" width={48} height={62} />
            {item.references.length > 1 && <span className={styles.referenceCount} aria-hidden>{item.references.length}</span>}
          </button>}
          {kind === 'original' && playback.error && <div role="status" className={styles.error}>
            {t('pairedPlaybackError')}
            <button type="button" onClick={async () => { await transport.current?.play(); }}>{t('retryPlayback')}</button>
          </div>}
        </div>
      </figure>)}
    </div>
    <dialog ref={dialog} className={styles.lightbox} aria-label={t('referenceImage', { index: referenceIndex + 1 })}
      onClick={event => { if (event.target === event.currentTarget) dialog.current?.close(); }}
      onClose={async () => { if (resumeAfterReference.current && !document.hidden) await transport.current?.play(); }}>
      <div className={styles.lightboxContent}>
        <button type="button" className={styles.close} onClick={() => dialog.current?.close()} aria-label={t('closeReference')}><X size={22} aria-hidden /></button>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={item.references[referenceIndex].url} alt={t('referenceImage', { index: referenceIndex + 1 })} />
        <p>{t('referencesUsed')} · {referenceIndex + 1} / {item.references.length}</p>
        {item.references.length > 1 && <div className={styles.referenceImages}>
          {item.references.map((reference, index) => <button type="button" key={reference.url} className={styles.referenceImage}
            onClick={() => setReferenceIndex(index)} aria-pressed={index === referenceIndex} aria-label={t('enlargeReference', { index: index + 1 })}>
            <Image unoptimized src={reference.url} alt={t('referenceImage', { index: index + 1 })} width={56} height={68} />
          </button>)}
        </div>}
      </div>
    </dialog>
  </div>;
}

export function KagemaneShowcase({ sourceLanding }: { sourceLanding?: '/ai-video-recreation' }) {
  const t = useTranslations('auth.cinematic.studioHomepage.work.motion');
  const locale = normalizeLocale(useLocale());
  const [selectedKey, setSelectedKey] = useState(cases[0].key);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const selectWithKeyboard = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % cases.length;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + cases.length) % cases.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = cases.length - 1;
    else return;
    event.preventDefault();
    setSelectedKey(cases[next].key);
    buttons.current[next]?.focus();
  };

  return <section id="kagemane" aria-labelledby="kagemane-title" className={`${base.showcaseScreen} ${styles.section}`} data-feature-showcase="video">
    <div className={base.container}>
      <header className={styles.heading}>
        <div className={`${base.sectionTitle} ${base.leftTitle} ${styles.title}`}>
          <p className={styles.eyebrow}>Kagemane</p>
          <h2 id="kagemane-title"><span data-motion="title-line">{t('title')}</span><em data-motion="title-line">{t('accent')}</em></h2>
          <p className={styles.body}>{t('showcaseBody')}</p>
        </div>
      </header>
      <div className={styles.layout}>
        <div className={styles.caseSelector}>
          <div className={styles.caseIndex} role="tablist" aria-label={t('casesLabel')}>
            {cases.map((item, index) => <button key={item.key} ref={element => { buttons.current[index] = element; }} type="button"
              role="tab" aria-label={t('caseNumber', { index: index + 1 })} id={`kagemane-tab-${item.key}`} aria-controls={`kagemane-panel-${item.key}`} aria-selected={item.key === selectedKey}
              tabIndex={item.key === selectedKey ? 0 : -1} onClick={() => setSelectedKey(item.key)} onKeyDown={event => selectWithKeyboard(event, index)}>
              <span className={styles.casePreview} data-layout={item.aspectRatio > 1 ? 'landscape' : 'portrait'} aria-hidden>
                <Image unoptimized src={item.thumbnails.original} alt="" width={72} height={128} />
                <Image unoptimized src={item.thumbnails.recreated} alt="" width={72} height={128} />
              </span>
              <span className={styles.caseNumber} aria-hidden>{String(index + 1).padStart(2, '0')}</span>
            </button>)}
            {Array.from({ length: Math.max(0, 9 - cases.length) }, (_, index) => <span key={`reserved-${index}`} className={styles.casePlaceholder} aria-hidden>
              <span className={styles.caseNumber}>{String(cases.length + index + 1).padStart(2, '0')}</span>
            </span>)}
          </div>
          {!sourceLanding && <StudioAction href={buildLocalizedSeoPath('/ai-video-recreation', locale)} variant="secondary" className={styles.cta}>{t('pageLink')}<ArrowUpRight size={18} aria-hidden /></StudioAction>}
        </div>
        <div className={styles.stage}>
          {cases.map(item => <div key={item.key} id={`kagemane-panel-${item.key}`} role="tabpanel" aria-labelledby={`kagemane-tab-${item.key}`} hidden={item.key !== selectedKey}>
            {item.key === selectedKey && <CaseComparison key={item.key} item={item} />}
          </div>)}
        </div>
      </div>
    </div>
  </section>;
}
